/**
 * Die Seiten des KI-Zugangs als nachgeladener Chunk: die Erlauben-Seite (`/ki-erlauben`) und "Meine KI-Zugaenge"
 * (`/ki-zugaenge`) gehoeren nicht ins Eintrittsbuendel (Grenze in `test/reference/bundle.test.ts`), erst der
 * Besuch einer der beiden Seiten laedt sie - gemeinsam, ueber `KiZugangSeiten.tsx` (ein Chunk). Gleiche Bauart wie
 * `Drawer/TrennenDialogLazy.tsx`; verwendet von `App/App.tsx`.
 *
 * Diese Datei importiert den Fehlerstapel NICHT (Rolldown-Falle, `AGENTS.md`): die Meldungen stehen auf den Seiten.
 *
 * ================= Wenn der Chunk nicht laedt =================
 *
 * Ein alter Tab nach einem Deployment kennt `KiZugangSeiten-<hash>.js` nicht mehr: `import()` scheitert, `React.lazy`
 * wirft beim Rendern. Ohne Grenze dazwischen faengt das erst die Wurzelgrenze (`FehlerGrenze` in `src/App.tsx`) und
 * ersetzt die GANZE Anwendung durch „Bitte die Seite neu laden“. `KiSeitenGrenze` zeigt die Meldung stattdessen auf
 * der Seite (Text von `fehlertext()`, bei einem Chunk-Ladefehler `FEHLERTEXTE.neuLaden`); Kopfzeile und Menue stehen
 * weiter. `React.lazy` merkt sich die Ablehnung: ein zweiter Versuch ohne Neuladen scheitert gleich wieder, die Meldung
 * bleibt dann dieselbe - darum der Knopf „Neu laden“ (`window.location.reload()`, wie in `DashboardPage/DashboardSeiteLazy.tsx`):
 * nur das Neuladen der Seite holt den neuen Stand, und der Kollege muss F5 nicht kennen.
 */
import { Component, lazy, Suspense } from "react";
import type { ErrorInfo, JSX, ReactNode } from "react";
import { fehlertext } from "../../lib/fehlertext.ts";
import type { KiErlaubenSeiteProps } from "./KiErlaubenSeite.tsx";
import type { KiZugaengeSeiteProps } from "./KiZugaengeSeite.tsx";

const ErlaubenChunk = lazy(() => import("./KiZugangSeiten.tsx").then((m) => ({ default: m.KiErlaubenSeite })));
const ZugaengeChunk = lazy(() => import("./KiZugangSeiten.tsx").then((m) => ({ default: m.KiZugaengeSeite })));

const FEHLER_KONTEXT = "Die Seite konnte nicht angezeigt werden.";

class KiSeitenGrenze extends Component<{ children: ReactNode }, { fehler: unknown; abgestuerzt: boolean }> {
  state = { fehler: null as unknown, abgestuerzt: false };

  static getDerivedStateFromError(fehler: unknown): { fehler: unknown; abgestuerzt: boolean } {
    return { fehler, abgestuerzt: true };
  }

  componentDidCatch(fehler: unknown, info: ErrorInfo): void {
    console.error("KI-Zugang-Seite abgestürzt:", fehler, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.abgestuerzt) return this.props.children;
    return (
      <div className="ki-shell">
        <div className="ladefehler" role="alert">
          <p className="ladefehler-text">{fehlertext(this.state.fehler, FEHLER_KONTEXT) ?? FEHLER_KONTEXT}</p>
          <button type="button" className="btn-ghost ladefehler-knopf" onClick={() => window.location.reload()}>
            Neu laden
          </button>
        </div>
      </div>
    );
  }
}

function Laden(): JSX.Element {
  return (
    <div className="ki-shell">
      <p className="ki-laden" role="status">Seite wird geladen …</p>
    </div>
  );
}

export function KiErlaubenSeiteLazy(props: KiErlaubenSeiteProps): JSX.Element {
  return (
    <KiSeitenGrenze>
      <Suspense fallback={<Laden />}>
        <ErlaubenChunk {...props} />
      </Suspense>
    </KiSeitenGrenze>
  );
}

export function KiZugaengeSeiteLazy(props: KiZugaengeSeiteProps): JSX.Element {
  return (
    <KiSeitenGrenze>
      <Suspense fallback={<Laden />}>
        <ZugaengeChunk {...props} />
      </Suspense>
    </KiSeitenGrenze>
  );
}
